import { useEffect, useState } from 'react';
import Head from 'components/Header/Head';
import Header from 'components/Header/Header';
import ArticleNav from 'components/Post/Article/ArticleNav';
import ArticleBody from 'components/Post/Article/ArticleBody';
import TfHighlights from 'components/Tools/BlogNameGenerator/TfHighlights';
import { getUser } from 'helpers/token';
import Footer from '../General/Footer';
import { fraunces } from '../Landing/fonts';
import { Eyebrow, MUTED } from '../Landing/ui';
import GuideMdx from './GuideMdx';

// Build-stable date formatting (no fromNow() on SSR'd pages); pinned to
// UTC so server and client format identical strings.
function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// Git-managed MDX guide at /blog/<slug> — the SEO half of the blog; DB
// announcements render through BlogPost instead.
export default function BlogGuidePost({ guide, images }) {
  const [isMember, setIsMember] = useState(false);

  useEffect(() => {
    if (getUser()) setIsMember(true);
  }, []);

  // Warm paper behind everything — same as the post page (SinglePost.js).
  useEffect(() => {
    document.body.classList.add('tf-paper');
    return () => document.body.classList.remove('tf-paper');
  }, []);

  // Frontmatter byline (E-E-A-T) — brand account when a guide has none.
  const author = guide.author
    ? {
        username: guide.author,
        displayName: guide.authorName || guide.author,
      }
    : { username: 'travelfeed', displayName: 'TravelFeed' };

  const canonicalUrl = `https://travelfeed.com/blog/${guide.slug}`;
  // Frontmatter thumbs are root-relative repo assets; the OG worker and
  // JSON-LD need absolute URLs.
  const thumbUrl =
    guide.thumb && guide.thumb.startsWith('/')
      ? `https://travelfeed.com${guide.thumb}`
      : guide.thumb;
  const generatedOgImage = `https://og.travelfeed.com/api/post?title=${encodeURIComponent(
    guide.title,
  )}${thumbUrl ? `&image=${encodeURIComponent(thumbUrl)}` : ''}`;

  return (
    <>
      {/* The minimal post object routes guides through Head's post.author
          path: og:type article + BlogPosting JSON-LD, same as DB blog posts */}
      <Head
        seo={{ canonicalUrl, metaDescription: guide.description }}
        shorttitle={guide.title}
        image={generatedOgImage}
        description={guide.description}
        post={{
          author,
          createdAt: guide.publishedAt,
          updatedAt: guide.updatedAt,
          tags: ['travel-blogging'],
          thumbnail: thumbUrl,
        }}
        faq={guide.faq}
      />
      {isMember ? <Header /> : <ArticleNav />}
      <div className="tw-bg-[#FAF8F1] dark:tw-bg-[#0E1A16]">
        <div className="tw-pt-20" />
        <header className="tw-mx-auto tw-max-w-[46rem] tw-px-4 tw-pb-8 tw-pt-8 sm:tw-pt-12">
          <Eyebrow>TravelFeed Journal</Eyebrow>
          <h1
            className={`${fraunces.className} tw-m-0 tw-text-3xl tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] sm:tw-text-4xl lg:tw-text-[2.75rem]`}
          >
            {guide.title}
          </h1>
          <p
            className={`${MUTED} tw-mb-0 tw-mt-4 tw-text-lg tw-leading-relaxed`}
          >
            {guide.description}
          </p>
          <p className="tw-mb-0 tw-mt-5 tw-font-mono tw-text-[11px] tw-uppercase tw-tracking-[0.18em] tw-text-[#8A958F]">
            {guide.author && (
              <>
                By{' '}
                <a
                  href={`/@${author.username}`}
                  className="tw-text-primary-700 dark:tw-text-primary-300 hover:tw-underline"
                >
                  {author.displayName}
                </a>
                {guide.authorTitle ? `, ${guide.authorTitle}` : ''} ·{' '}
              </>
            )}
            Updated {formatDate(guide.updatedAt)}
            {guide.readTime
              ? ` · ${Math.max(1, Math.round(guide.readTime))} min read`
              : ''}
          </p>
        </header>
        <ArticleBody>
          <GuideMdx code={guide.body.code} images={images} />
          {/* The frontmatter FAQs feed FAQPage JSON-LD in Head — schema
              content must also be visible on the page, so render them */}
          {guide.faq && guide.faq.length > 0 && (
            <>
              <h2 id="faq">Frequently asked questions</h2>
              {guide.faq.map(entry => (
                <div key={entry.questionName}>
                  <h3>{entry.questionName}</h3>
                  <p>{entry.acceptedAnswerText}</p>
                </div>
              ))}
            </>
          )}
        </ArticleBody>
        <div className="tw-pt-10" />
      </div>
      {/* Wave must match the tf-paper article background above it */}
      <TfHighlights topFill="tw-fill-[#FAF8F1] dark:tw-fill-[#0E1A16]" />
      <div className="tw-pt-8" />
      <Footer isNoSpace />
    </>
  );
}
